import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getPaymentMethods, createDeposit, getApi } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import swal from 'sweetalert2';

const Deposit = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        amount: '',
        paymentMethodId: '',
        transactionId: '',
        medium: 'bank_transfer'
    });
    const [limits, setLimits] = useState(null);
    const [fees, setFees] = useState(null);
    const [systemStatus, setSystemStatus] = useState(null);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [depositResult, setDepositResult] = useState(null);
    const [kycStatus, setKycStatus] = useState(null);

    const fileRef = useRef();

    useEffect(() => {
        if (auth.isAuthenticated && auth.token) {
            fetchPaymentMethods();
            fetchKYCStatus();
        } else {
            navigate('/login', { replace: true });
        }
    }, [auth, navigate]);

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPaymentMethods();

            if (response.success) {
                const methods = response.data?.paymentMethods || [];
                const activeMethod = methods.find(m => m.status === 'active');

                setPaymentMethods(methods);
                setLimits(response.data?.limits);
                setFees(response.data?.fees);
                setSystemStatus(response.data?.systemStatus);
                setVerificationStatus(response.data?.verificationStatus);

                if (activeMethod) {
                    setFormData(prev => ({
                        ...prev,
                        paymentMethodId: activeMethod._id
                    }));
                    setSelectedMethod(activeMethod);
                }
            } else {
                throw new Error(response.message || 'Failed to fetch payment methods');
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setError(error.message);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message
            });
            setPaymentMethods([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchKYCStatus = async () => {
        try {
            const response = await getApi('kyc/details');
            // console.log('KYC API Response:', response);
            if (response.success && response.data?.kyc?.status) {
                // console.log('Setting KYC Status to:', response.data.kyc.status);
                setKycStatus(response.data.kyc.status);
            } else {
                // console.log('Setting KYC Status to: not_found');
                setKycStatus('not_found');
            }
        } catch (error) {
            console.error('Error fetching KYC status:', error);
            console.log('Setting KYC Status to: not_found due to error');
            setKycStatus('not_found');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        console.log(`Setting ${name} to:`, value);
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'paymentMethodId') {
            const method = paymentMethods.find(m => m._id === value);
            setSelectedMethod(method);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                receipt: file
            }));
        }
    };

    const TRANSACTION_MEDIUMS = [
        { value: 'UPI', label: 'UPI' },
        { value: 'NEFT', label: 'NEFT' },
        { value: 'RTGS', label: 'RTGS' },
        { value: 'IMPS', label: 'IMPS' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'credit', label: 'Credit' },
        { value: 'crypto', label: 'Crypto' },
        { value: 'bonus', label: 'Bonus' },
        { value: 'referral_bonus', label: 'Referral Bonus' },
        { value: 'demat_transfer', label: 'Demat Transfer' },
    ];

    const validateForm = () => {
        if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid amount'
            });
            return false;
        }

        if (!formData.paymentMethodId) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please select a payment method'
            });
            return false;
        }

        if (!formData.transactionId) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter the bank transaction ID'
            });
            return false;
        }

        if (!formData.medium) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please select a transaction medium'
            });
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);

            const response = await createDeposit(formData);
            console.log('Deposit response:', response);

            if (response.success) {
                const depositData = response.data;

                // Store the result with safe values
                setDepositResult({
                    id: depositData.depositId,
                    reference: depositData.reference,
                    amount: Number(depositData.amount),
                    status: depositData.status,
                    medium: depositData.medium,
                    createdAt: depositData.createdAt
                });

                // Show success message with project's UI style
                swal.fire({
                    title: '<strong>Deposit Submitted</strong>',
                    icon: 'success',
                    html: `
                        <div style="text-align: left; padding: 10px;">
                            <div style="color: #2bc155; margin-bottom: 15px; font-weight: 500;">
                                ${response.message}
                            </div>
                            <table class="table table-striped" style="margin-bottom: 15px;">
                                <tr>
                                    <td><strong>Deposit ID:</strong></td>
                                    <td class="text-end">${depositData.depositId}</td>
                                </tr>
                                <tr>
                                    <td><strong>Reference:</strong></td>
                                    <td class="text-end">${depositData.reference}</td>
                                </tr>
                                <tr>
                                    <td><strong>Amount:</strong></td>
                                    <td class="text-end">${Number(depositData.amount).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}</td>
                                </tr>
                                <tr>
                                    <td><strong>Medium:</strong></td>
                                    <td class="text-end">${depositData.medium}</td>
                                </tr>
                                <tr>
                                    <td><strong>Date:</strong></td>
                                    <td class="text-end">${new Date(depositData.createdAt).toLocaleString('en-IN')}</td>
                                </tr>
                                <tr>
                                    <td><strong>Status:</strong></td>
                                    <td class="text-end">
                                        <span class="badge badge-${depositData.status === 'pending' ? 'warning' :
                            depositData.status === 'approved' ? 'success' :
                                depositData.status === 'rejected' ? 'danger' : 'secondary'
                        }">
                                            ${depositData.status.toUpperCase()}
                                        </span>
                                    </td>
                                </tr>
                            </table>
                            <div style="color: #666; font-size: 12px; text-align: center;">
                                Please save this information for future reference
                            </div>
                        </div>
                    `,
                    showCloseButton: true,
                    showCancelButton: false,
                    focusConfirm: false,
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#2bc155',
                    customClass: {
                        container: 'custom-swal-container',
                        popup: 'custom-swal-popup',
                        header: 'custom-swal-header',
                        title: 'custom-swal-title',
                        closeButton: 'custom-swal-close',
                        content: 'custom-swal-content',
                        confirmButton: 'btn btn-success',
                    }
                });

                // Reset form
                setFormData({
                    amount: '',
                    paymentMethodId: selectedMethod?._id || '',
                    transactionId: '',
                    medium: 'bank_transfer'
                });

                // Refresh payment methods to get updated limits
                fetchPaymentMethods();

            } else {
                throw new Error(response.message || 'Failed to submit deposit request');
            }
        } catch (error) {
            console.error('Error creating deposit:', error);
            setError(error.message);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to submit deposit request',
                confirmButtonColor: '#ff4f4f'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const calculateFees = (amount) => {
        if (!fees || !amount) return 0;
        const percentageFee = (amount * fees.percentage) / 100;
        const totalFee = percentageFee + fees.fixed;
        return Math.min(Math.max(totalFee, fees.min), fees.max);
    };

    if (loading) return <Loading />;

    if (!systemStatus?.depositsEnabled) {
        return (
            <>
                <PageTitle activeMenu="Deposit" motherMenu="Wallet" />
                <Card>
                    <Card.Body>
                        <Alert variant="warning">
                            Deposits are currently disabled. Please try again later.
                        </Alert>
                    </Card.Body>
                </Card>
            </>
        );
    }

    if (systemStatus?.maintenanceMode) {
        return (
            <>
                <PageTitle activeMenu="Deposit" motherMenu="Wallet" />
                <Card>
                    <Card.Body>
                        <Alert variant="info">
                            System is under maintenance. Please try again later.
                        </Alert>
                    </Card.Body>
                </Card>
            </>
        );
    }

    return (
        <div className="deposit-page">
            <PageTitle activeMenu="Deposit" motherMenu="Wallet" />

            {(kycStatus && kycStatus !== 'approved') && (
                <Alert variant="warning" className="mb-4">
                    Please complete your KYC verification to enable all deposit features.
                </Alert>
            )}

            <div className="row g-3">
                <div className="col-xxl-4 col-xl-4 col-lg-5">
                    <div className="sticky-top" style={{ top: '20px', zIndex: 1 }}>
                        {selectedMethod && (
                            <Card>
                                <Card.Header>
                                    <Card.Title>Payment Details</Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <div className="payment-method-details">
                                        {selectedMethod.paymentTypes?.upi && (
                                            <div className="upi-details mb-4">
                                                <h5>QR Code</h5>
                                                {selectedMethod.upiQrCode && (
                                                    <div className="text-center mb-3">
                                                        <img
                                                            src={process.env.REACT_APP_IMAGE_URL + selectedMethod.upiQrCode}
                                                            alt="QR Code"
                                                            style={{
                                                                maxWidth: '200px',
                                                                width: '100%',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {selectedMethod.upiId && (
                                                    <div className="upi-id-container">
                                                        <p className="mb-2">Address:</p>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <code className="fs-5">{selectedMethod.upiId}</code>
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(selectedMethod.upiId);
                                                                    swal.fire({
                                                                        icon: 'success',
                                                                        title: 'Copied!',
                                                                        text: 'Address copied to clipboard',
                                                                        showConfirmButton: false,
                                                                        timer: 1500
                                                                    });
                                                                }}
                                                            >
                                                                Copy
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {selectedMethod.paymentTypes?.bankTransfer && (
                                            <div className="bank-details">
                                                <h5>Bank Details</h5>
                                                <table className="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td>Account Name:</td>
                                                            <td>{selectedMethod.accountHolderName}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Bank Name:</td>
                                                            <td>{selectedMethod.bankName}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Account No:</td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <code>{selectedMethod.accountNumber}</code>
                                                                    <Button
                                                                        variant="light"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(selectedMethod.accountNumber);
                                                                            swal.fire({
                                                                                icon: 'success',
                                                                                title: 'Copied!',
                                                                                text: 'Account number copied to clipboard',
                                                                                showConfirmButton: false,
                                                                                timer: 1500
                                                                            });
                                                                        }}
                                                                    >
                                                                        Copy
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>IFSC Code:</td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <code>{selectedMethod.ifscCode}</code>
                                                                    <Button
                                                                        variant="light"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(selectedMethod.ifscCode);
                                                                            swal.fire({
                                                                                icon: 'success',
                                                                                title: 'Copied!',
                                                                                text: 'IFSC code copied to clipboard',
                                                                                showConfirmButton: false,
                                                                                timer: 1500
                                                                            });
                                                                        }}
                                                                    >
                                                                        Copy
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {(selectedMethod.cryptoAddress || selectedMethod.network || selectedMethod.cryptoQR) && (
                                            <div className="crypto-details mb-4">
                                                <h5>Crypto Details</h5>
                                                {selectedMethod.cryptoAddress && (
                                                    <div className="mb-3">
                                                        <p className="mb-2">Crypto Address:</p>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <code className="fs-6">{selectedMethod.cryptoAddress}</code>
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(selectedMethod.cryptoAddress);
                                                                    swal.fire({
                                                                        icon: 'success',
                                                                        title: 'Copied!',
                                                                        text: 'Crypto address copied to clipboard',
                                                                        showConfirmButton: false,
                                                                        timer: 1500
                                                                    });
                                                                }}
                                                            >
                                                                Copy
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedMethod.network && (
                                                    <div className="mb-3">
                                                        <p className="mb-2">Network:</p>
                                                        <span className="badge bg-primary">{selectedMethod.network}</span>
                                                    </div>
                                                )}
                                                {selectedMethod.cryptoQR && (
                                                    <div className="mb-3">
                                                        <p className="mb-2">Crypto QR Code:</p>
                                                        <div className="text-center">
                                                            <img
                                                                src={process.env.REACT_APP_IMAGE_URL + selectedMethod.cryptoQR}
                                                                alt="Crypto QR Code"
                                                                style={{
                                                                    maxWidth: '200px',
                                                                    width: '100%',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {limits && (
                                        <Card className="mt-3">
                                            <Card.Header>
                                                <Card.Title>Deposit Limits</Card.Title>
                                            </Card.Header>
                                            <Card.Body>
                                                <div className="limits-info">
                                                    <div className="limit-item mb-3 d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <small className="d-block text-muted">Min Deposit</small>
                                                            <h6 className="mb-0">${limits.minDeposit}</h6>
                                                        </div>
                                                        <div className="text-end">
                                                            <small className="d-block text-muted">Max Deposit</small>
                                                            <h6 className="mb-0">${limits.maxDeposit}</h6>
                                                        </div>
                                                    </div>

                                                    {(limits.dailyRemaining || limits.monthlyRemaining) && (
                                                        <div className="remaining-limits mt-3 pt-3 border-top">
                                                            {limits.dailyRemaining && (
                                                                <div className="mb-2">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <small className="text-muted">Daily Remaining</small>
                                                                        <h6 className="mb-0">${limits.dailyRemaining}</h6>
                                                                    </div>
                                                                    <div className="progress mt-2" style={{ height: '6px' }}>
                                                                        <div
                                                                            className="progress-bar bg-success"
                                                                            style={{
                                                                                width: `${(limits.dailyRemaining / limits.maxDeposit) * 100}%`
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {limits.monthlyRemaining && (
                                                                <div>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <small className="text-muted">Monthly Remaining</small>
                                                                        <h6 className="mb-0">${limits.monthlyRemaining}</h6>
                                                                    </div>
                                                                    <div className="progress mt-2" style={{ height: '6px' }}>
                                                                        <div
                                                                            className="progress-bar bg-info"
                                                                            style={{
                                                                                width: `${(limits.monthlyRemaining / limits.maxDeposit) * 100}%`
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    )}
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                </div>

                <div className="col-xxl-8 col-xl-8 col-lg-7">
                    <Card>
                        <Card.Header>
                            <Card.Title>Deposit Funds</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {!selectedMethod ? (
                                <Alert variant="warning">
                                    No payment methods available at the moment.
                                </Alert>
                            ) : (
                                <Form onSubmit={handleSubmit} className="deposit-form">
                                    <Form.Group className="mb-4">
                                        <Form.Label>Amount</Form.Label>
                                        <div className="amount-input-wrapper">
                                            <Form.Control
                                                type="number"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                min={limits?.minDeposit || 1}
                                                max={limits?.maxDeposit}
                                                required
                                                className="form-control-lg"
                                            />
                                            {fees && formData.amount && (
                                                <div className="fee-info mt-2">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Amount:</span>
                                                        <span>${formData.amount}</span>
                                                    </div>
                                                    {fees.percentage > 0 && (
                                                        <div className="d-flex justify-content-between mt-1">
                                                            <span className="text-muted">Fee ({fees.percentage}%):</span>
                                                            <span>${calculateFees(formData.amount)}</span>
                                                        </div>
                                                    )}
                                                    {fees.fixed > 0 && (
                                                        <div className="d-flex justify-content-between mt-1">
                                                            <span className="text-muted">Fixed Fee:</span>
                                                            <span>${fees.fixed}</span>
                                                        </div>
                                                    )}
                                                    <div className="d-flex justify-content-between mt-1 pt-2 border-top">
                                                        <span className="text-muted">Total:</span>
                                                        <span className="font-w600">
                                                            ${Number(formData.amount) + calculateFees(formData.amount)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Transaction Medium</Form.Label>
                                        <Form.Select
                                            name="medium"
                                            value={formData.medium}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control-lg"
                                        >
                                            {TRANSACTION_MEDIUMS.map(medium => (
                                                <option key={medium.value} value={medium.value}>
                                                    {medium.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Select the method used for bank transfer
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Transaction ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="transactionId"
                                            value={formData.transactionId}
                                            onChange={handleInputChange}
                                            required
                                            placeholder={`Enter ${formData.medium} transaction ID`}
                                            className="form-control-lg"
                                        />
                                        <Form.Text className="text-muted">
                                            Enter the transaction ID from your {formData.medium} transfer
                                        </Form.Text>
                                    </Form.Group>

                                    <div className="text-center mt-4">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="lg"
                                            disabled={submitting ||
                                                !selectedMethod ||
                                                limits?.limitsExceeded?.daily ||
                                                limits?.limitsExceeded?.monthly}
                                        >
                                            {submitting ? 'Processing...' : 'Submit Deposit'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <style jsx>{`
                .deposit-page {
                    min-height: calc(100vh - 200px);
                    padding-bottom: 2rem;
                }
                .payment-method-details {
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 0.5rem;
                }
                .limits-info {
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 0.5rem;
                }
                .amount-input-wrapper {
                    max-width: 400px;
                }
                .fee-info {
                    background: #f8f9fa;
                    padding: 0.75rem;
                    border-radius: 0.5rem;
                    font-size: 0.875rem;
                }
                @media (max-width: 991px) {
                    .sticky-top {
                        position: relative !important;
                        top: 0 !important;
                        margin-bottom: 1rem;
                    }
                }
                .custom-swal-container {
                    z-index: 9999;
                }
                .custom-swal-popup {
                    border-radius: 1rem;
                    padding: 1rem;
                }
                .custom-swal-header {
                    border-bottom: 1px solid #eee;
                }
                .custom-swal-title {
                    color: #333;
                    font-size: 1.25rem;
                }
                .custom-swal-close {
                    color: #666;
                }
                .custom-swal-content {
                    padding: 1rem 0;
                }
                .table {
                    margin-bottom: 0;
                }
                .table td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-color: #eee;
                }
                .badge {
                    padding: 0.5em 0.75em;
                    font-weight: 500;
                }
                .badge-success {
                    background-color: #2bc155;
                }
                .badge-warning {
                    background-color: #ffb800;
                    color: #fff;
                }
                .badge-danger {
                    background-color: #ff4f4f;
                }
                .badge-secondary {
                    background-color: #666;
                }
                .upi-details {
                    border-bottom: 1px solid #dee2e6;
                }
                .upi-id-container {
                    background: #fff;
                    padding: 1rem;
                    border-radius: 0.5rem;
                }
                code {
                    background: #e9ecef;
                    padding: 0.2rem 0.4rem;
                    border-radius: 0.25rem;
                }
            `}</style>
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(Deposit); 